import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { toast } from "sonner";
import { z } from "zod";
import {
  ConfiguredState,
  Control,
  ControlSchema,
  ControlWithProcedure,
  ControlWithProcedureSchema,
  ControlWithUser,
  ControlWithUserSchema,
  FilterOptions,
  FilterOptionsSchema,
} from "../models/audit/control";
import { FyCycleOptionSchema } from "../models/audit/fyCycleOption";
import {
  mergeOptroAccessUnavailable,
  readOptroAccessUnavailable,
} from "../lib/optroAccessListFlag";
import {
  TestPhase,
  testPhaseValues,
  ControlSessionWithUser,
  ControlSessionWithUserSchema,
} from "../models/audit/controlSession";
import { useNavigate } from "react-router-dom";
import {
  ListFetchMode,
  mergeListPage,
  nextListOffset,
} from "../lib/listPaging";

export type ControlsListFilters = {
  searchTerm?: string;
  userIds?: string[];
  startDate?: Date;
  endDate?: Date;
  includeNonTemplates?: boolean;
  configuredStates?: ConfiguredState[];
  cycles?: string[];
  processes?: string[];
  controlOwners?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

// Query string for one limit/offset window of GET /controls/v2/.
const buildControlsListParams = (
  filters: ControlsListFilters,
  limit: number,
  offset: number,
): URLSearchParams => {
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (filters.searchTerm) {
    queryParams.append("search", filters.searchTerm);
  }

  // Multi-value filters
  if (filters.configuredStates && filters.configuredStates.length > 0) {
    filters.configuredStates.forEach((state) => {
      queryParams.append("configured_states", state);
    });
  }

  if (filters.cycles && filters.cycles.length > 0) {
    filters.cycles.forEach((cycle) => {
      queryParams.append("cycles", cycle);
    });
  }

  if (filters.processes && filters.processes.length > 0) {
    filters.processes.forEach((process) => {
      queryParams.append("processes", process);
    });
  }

  if (filters.controlOwners && filters.controlOwners.length > 0) {
    filters.controlOwners.forEach((owner) => {
      queryParams.append("control_owners", owner);
    });
  }

  // Sorting parameters
  if (filters.sortBy) {
    queryParams.append("sort_by", filters.sortBy);
  }

  if (filters.sortOrder) {
    queryParams.append("sort_order", filters.sortOrder);
  }

  return queryParams;
};

// The hook supports backend pagination (limit/offset) and server-side search.
// It exposes a `loadMore` function and `hasMore` flag for append-only "Load more" paging.
export const useControlsV2 = (filters: ControlsListFilters) => {
  const LIMIT = 25;

  const [controls, setControls] = useState<ControlWithUser[]>([]);
  const [totalControlsCount, setTotalControlsCount] = useState(0);
  // See OptroAccessListFlagSchema: linked rows are hidden from this list.
  // Held until the list resets (mergeOptroAccessUnavailable).
  const [optroAccessUnavailable, setOptroAccessUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  // The control read's own failure, beside its `loading`. A surface rendering
  // the list needs it to tell a failed read from a search that matched no
  // control: the toast says a read failed, not that this list is unknown.
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const isLoadingRef = useRef(false);
  // A reset requested while a fetch is in flight is queued, not dropped:
  // the in-flight guard below used to silently swallow `refetch()` (e.g.
  // the import-complete refresh racing an infinite-scroll loadMore), and
  // that refresh is one-shot — the newly imported controls would never
  // appear until some other action re-fetched.
  const pendingResetRef = useRef(false);
  // Same for a first-page refresh; a queued reset supersedes it.
  const pendingRefreshRef = useRef(false);

  // Store filters in ref to avoid recreating fetchControls
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Serialize filters for comparison
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchControls = useCallback(async (mode: ListFetchMode) => {
    if (isLoadingRef.current) {
      if (mode === "reset") {
        pendingResetRef.current = true;
      } else if (mode === "refresh") {
        pendingRefreshRef.current = true;
      }
      return;
    }

    isLoadingRef.current = true;
    const currentOffset = mode === "more" ? offsetRef.current : 0;
    const currentFilters = filtersRef.current;

    try {
      setLoading(true);

      const queryParams = buildControlsListParams(
        currentFilters,
        LIMIT,
        currentOffset,
      );

      const response = await fetchFastApi({
        path: `/controls/v2/?${queryParams.toString()}`,
        method: "GET",
      });

      const data = z.array(ControlWithUserSchema).parse(response.controls);
      const totalCount = response.totalCount;
      setTotalControlsCount(totalCount);
      const pageUnavailable = readOptroAccessUnavailable(response);
      setOptroAccessUnavailable((current) =>
        mergeOptroAccessUnavailable(mode === "reset", current, pageUnavailable),
      );

      setControls((prev) => mergeListPage(mode, prev, data));

      // Determine if more data is available. A refresh past the first page
      // leaves it to the pages already loaded.
      if (mode !== "refresh" || offsetRef.current <= LIMIT) {
        setHasMore(data.length === LIMIT);
      }

      // Update offset for the next fetch
      offsetRef.current = nextListOffset(
        mode,
        offsetRef.current,
        currentOffset,
        LIMIT,
      );
      setError(null);
    } catch (error) {
      console.error("Failed to fetch controls:", error);
      setError(error instanceof Error ? error : new Error(String(error)));
      toast.error("Failed to fetch controls");
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      if (pendingResetRef.current) {
        pendingResetRef.current = false;
        pendingRefreshRef.current = false;
        offsetRef.current = 0;
        setHasMore(true);
        void fetchControls("reset");
      } else if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        void fetchControls("refresh");
      }
    }
  }, []);

  // Initial fetch & refetch when filters change
  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setControls([]);
    fetchControls("reset");
  }, [filtersKey, fetchControls]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    fetchControls("more");
  }, [hasMore, fetchControls]);

  const refetch = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    fetchControls("reset");
  }, [fetchControls]);

  // Reloads the first page in place, keeping later pages already scrolled
  // in — for picking up newly created rows without resetting the list.
  const refresh = useCallback(() => {
    void fetchControls("refresh");
  }, [fetchControls]);

  return {
    controls,
    totalControlsCount,
    optroAccessUnavailable,
    loading,
    error,
    loadMore,
    hasMore,
    refetch,
    refresh,
  };
};

export const CONTROLS_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 150, 300];
export const DEFAULT_CONTROLS_ROWS_PER_PAGE = 25;

const ControlsListTotalSchema = z.object({ totalCount: z.number() });

// The Controls table's listing: each page replaces the rows, Optro-style.
// Same endpoint and filters as useControlsV2, which keeps the append-only
// "Load more" paging the control picker uses.
export const useControlsPage = (
  filters: ControlsListFilters,
  initial: { page: number; rowsPerPage: number },
) => {
  // 1-based page window.
  const [page, setPage] = useState(initial.page);
  const [rowsPerPage, setRowsPerPage] = useState(initial.rowsPerPage);

  const [controls, setControls] = useState<ControlWithUser[]>([]);
  const [totalControlsCount, setTotalControlsCount] = useState(0);
  // See OptroAccessListFlagSchema. Each page is a fresh read, so the flag is
  // the latest page's own rather than merged across pages.
  const [optroAccessUnavailable, setOptroAccessUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Incremented on every fetch, reset, and unmount so a slow in-flight
  // response can't stomp the state of a newer page or filter set.
  const generationRef = useRef(0);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // Last query inputs, so a filter or page-size change resets to page 1
  // without fetching the stale page. Seeded from the initial values so a page
  // restored from the URL survives the first render.
  const lastQueryRef = useRef({ filtersKey, rowsPerPage });

  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;

    const queryChanged =
      lastQueryRef.current.filtersKey !== filtersKey ||
      lastQueryRef.current.rowsPerPage !== rowsPerPage;
    lastQueryRef.current = { filtersKey, rowsPerPage };
    if (queryChanged && page !== 1) {
      // The fetch happens on the effect re-run the reset triggers.
      setPage(1);
      return;
    }

    const currentFilters = filtersRef.current;

    const fetchPage = async () => {
      setLoading(true);
      // True when this fetch hands off to another page's fetch, which then
      // owns `loading`.
      let movedPage = false;
      try {
        const queryParams = buildControlsListParams(
          currentFilters,
          rowsPerPage,
          (page - 1) * rowsPerPage,
        );
        const response = await fetchFastApi({
          path: `/controls/v2/?${queryParams.toString()}`,
          method: "GET",
        });
        if (generationRef.current !== generation) return;
        const data = z.array(ControlWithUserSchema).parse(response.controls);
        let { totalCount } = ControlsListTotalSchema.parse(response);
        // Past the end (rows deleted, stale URL): jump to the last real page.
        // An empty page reports totalCount 0, so read the real total from a
        // one-row request.
        if (data.length === 0 && page > 1) {
          const firstRow = await fetchFastApi({
            path: `/controls/v2/?${buildControlsListParams(currentFilters, 1, 0).toString()}`,
            method: "GET",
          });
          if (generationRef.current !== generation) return;
          totalCount = ControlsListTotalSchema.parse(firstRow).totalCount;
          const lastPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
          if (lastPage < page) {
            movedPage = true;
            setPage(lastPage);
            return;
          }
        }
        setControls(data);
        setTotalControlsCount(totalCount);
        setOptroAccessUnavailable(readOptroAccessUnavailable(response));
        setError(null);
      } catch (err) {
        if (generationRef.current !== generation) return;
        console.error("Failed to fetch controls:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        toast.error("Failed to fetch controls");
      } finally {
        if (generationRef.current === generation && !movedPage) {
          setLoading(false);
        }
      }
    };

    void fetchPage();
  }, [filtersKey, page, rowsPerPage, attempt]);

  // Re-reads the current page in place, e.g. as imported controls land.
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    controls,
    totalControlsCount,
    optroAccessUnavailable,
    loading,
    error,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    reload,
  };
};

// Hook for creating a new control
export const useCreateControl = () => {
  const [loading, setLoading] = useState(false);

  const createControl = useCallback(
    async (
      name: string,
      description: string,
      controlType: string = "itgc",
    ): Promise<Control | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/controls/v2/",
          method: "POST",
          body: {
            name,
            description,
            control_type: controlType,
          },
        });

        toast.success("Control created successfully");
        return ControlSchema.parse(response.control);
      } catch (error) {
        console.error("Failed to create control:", error);
        toast.error("Failed to create control");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createControl,
    loading,
  };
};

// Hook for fetching a single control with its procedures (for configuration page)
export const useControlWithProcedures = (controlId: string | null) => {
  const [controlWithProcedures, setControlWithProcedures] =
    useState<ControlWithProcedure | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchControlWithProcedures = useCallback(
    async ({ backgroundFetch = false }: { backgroundFetch?: boolean } = {}) => {
      if (!controlId) return;

      if (!backgroundFetch) {
        setLoading(true);
      }
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/${controlId}`,
          method: "GET",
        });

        setControlWithProcedures(
          ControlWithProcedureSchema.parse(response.control),
        );
      } catch (error) {
        console.error("Failed to fetch control with procedures:", error);
        toast.error("Failed to fetch control");
      } finally {
        setLoading(false);
      }
    },
    [controlId],
  );

  useEffect(() => {
    fetchControlWithProcedures({ backgroundFetch: false });
  }, [fetchControlWithProcedures]);

  return {
    controlWithProcedures,
    loading,
    refetch: fetchControlWithProcedures,
  };
};

// Discriminated union for control update results
export type UpdateControlResult = { type: "updated"; control: Control } | null;

// Hook for updating a control
export const useUpdateControl = () => {
  const [loading, setLoading] = useState(false);

  const updateControl = useCallback(
    async (
      controlId: string,
      updates: {
        name: string;
        description: string;
        control_type?: string;
        configured_state?: ConfiguredState;
        cycle?: string;
        process?: string;
        control_owner?: string;
      },
    ): Promise<UpdateControlResult> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/control/${controlId}`,
          method: "PUT",
          body: updates,
        });

        toast.success("Control updated successfully");
        return {
          type: "updated",
          control: ControlSchema.parse(response.control),
        };
      } catch (error) {
        console.error("Failed to update control:", error);
        toast.error("Failed to update control");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateControl,
    loading,
  };
};

// Hook for fetching a single control with its sessions
export const useControlV2 = (controlId: string | null) => {
  const [control, setControl] = useState<Control | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchControl = useCallback(async () => {
    if (!controlId) return;

    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/controls/v2/${controlId}`,
        method: "GET",
      });

      setControl(ControlSchema.parse(response.control));
    } catch (error) {
      console.error("Failed to fetch control:", error);
      toast.error("Failed to fetch control");
    } finally {
      setLoading(false);
    }
  }, [controlId]);

  useEffect(() => {
    fetchControl();
  }, [fetchControl]);

  return {
    control,
    loading,
    refetch: fetchControl,
  };
};

// Hook for creating control sessions
export const useCreateControlSession = () => {
  const [loading, setLoading] = useState(false);

  const createSession = useCallback(
    async (
      controlId: string,
      sessionName: string,
      testPhase: TestPhase,
      fyCycleId?: string | null,
      optroTestId?: number | null,
    ): Promise<string | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/control/${controlId}/create-session`,
          method: "POST",
          throwOnErrorEnvelope: true,
          body: {
            session_name: sessionName,
            test_phase: testPhase,
            fy_cycle_id: fyCycleId ?? null,
            ...(optroTestId != null ? { optro_test_id: optroTestId } : {}),
          },
        });

        toast.success("Session created successfully");
        return response.session_id;
      } catch (error) {
        console.error("Failed to create session:", error);

        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create session");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createSession,
    loading,
  };
};

// Hook for fetching control sessions (tests)
export const useControlSessions = (controlId: string | null) => {
  // Sessions are kept with the control they were read for, so a switch to
  // another control never shows the previous control's tests while the new
  // read is in flight, and a slow read for an old control cannot land late.
  const [loaded, setLoaded] = useState<{
    controlId: string;
    sessions: ControlSessionWithUser[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const latestControlIdRef = useRef(controlId);
  latestControlIdRef.current = controlId;

  const fetchSessions = useCallback(async () => {
    // A refetch retained by a callback that outlived its control (a create or
    // delete that resolves after the user moved on) must not touch state.
    if (latestControlIdRef.current !== controlId) return;
    if (!controlId) {
      setLoaded(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/controls/v2/control/${controlId}/sessions`,
        method: "GET",
      });

      const data = z
        .array(ControlSessionWithUserSchema)
        .parse(response.sessions || []);
      if (latestControlIdRef.current !== controlId) return;
      setLoaded({ controlId, sessions: data });
    } catch (error) {
      if (latestControlIdRef.current !== controlId) return;
      console.error("Failed to fetch control sessions:", error);
      toast.error("Failed to fetch control sessions");
      setLoaded({ controlId, sessions: [] });
    } finally {
      if (latestControlIdRef.current === controlId) setLoading(false);
    }
  }, [controlId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const isCurrent = loaded !== null && loaded.controlId === controlId;

  return {
    sessions: isCurrent ? loaded.sessions : [],
    loading: loading || (controlId !== null && !isCurrent),
    refetch: fetchSessions,
  };
};

// Hook for AI-generating a control from uploaded files
export const useGenerateControl = () => {
  const [loading, setLoading] = useState(false);

  const generateControl = useCallback(
    async (params: {
      controlDefFiles: File[];
      pastWorkPaperFiles?: File[];
      additionalControlDetails?: string;
      generatePbc?: boolean;
    }): Promise<string | null> => {
      setLoading(true);
      try {
        const formData = new FormData();
        // Append multiple control definition files
        params.controlDefFiles.forEach((file) => {
          formData.append("control_def_files", file);
        });
        // Append multiple past workpaper files if provided
        if (params.pastWorkPaperFiles && params.pastWorkPaperFiles.length > 0) {
          params.pastWorkPaperFiles.forEach((file) => {
            formData.append("past_work_paper_files", file);
          });
        }
        if (params.additionalControlDetails) {
          formData.append(
            "additional_template_details",
            params.additionalControlDetails,
          );
        }
        if (params.generatePbc) {
          formData.append("generate_pbc", params.generatePbc.toString());
        }

        const response = await fetchFastApi({
          path: "/controls/v2/generate-control",
          method: "POST",
          body: formData,
        });
        const taskKey =
          response?.taskKey || response?.task_id || response?.taskId;

        if (!taskKey) {
          toast.error("Failed to generate control: missing task key");
          return null;
        }

        return taskKey as string;
      } catch (error) {
        console.error("Failed to generate control:", error);
        toast.error("Failed to generate control");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    generateControl,
    loading,
  };
};

// Hook to poll the generate-control task status until completion
export const usePollGenerateControlStatus = () => {
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const pollTask = useCallback(
    async (
      taskKey: string,
      {
        intervalMs = 5000,
        timeoutMs = 10 * 60 * 1000,
      }: { intervalMs?: number; timeoutMs?: number } = {},
    ): Promise<{
      status: "success" | "failure" | "timeout";
      controlId?: string;
    }> => {
      if (isMountedRef.current) setLoading(true);
      const toastId = `generate-control:${taskKey}`;
      toast.loading("Generating control…", {
        id: toastId,
        description:
          "We're creating your control. This may take a few minutes.",
      });
      const start = Date.now();
      let lastStatus: "success" | "failure" | "timeout" | null = null;

      while (Date.now() - start < timeoutMs) {
        try {
          console.log("polling task", taskKey);
          const response = await fetchFastApi({
            path: `/controls/v2/generate-control/${encodeURIComponent(taskKey)}`,
            method: "GET",
          });
          const statusRaw: string = (
            response?.response?.status || "unknown"
          ).toString();
          const status = statusRaw.toLowerCase();
          lastStatus = status as "success" | "failure" | "timeout";

          if (status === "success") {
            if (isMountedRef.current) setLoading(false);
            const controlId = response?.response?.control_id;
            toast.success("Control generated", {
              id: toastId,
              description: "Your control is ready.",
              duration: 10000,
              action: {
                label: "Open control",
                onClick: () => {
                  if (controlId) {
                    navigate(`/audit/controls/v2/${controlId}`);
                  }
                },
              },
            });
            return {
              status: "success",
              controlId,
            };
          }
          if (status === "failure" || status === "cancelled") {
            if (isMountedRef.current) setLoading(false);
            toast.error("Failed to generate control", { id: toastId });
            return {
              status: "failure",
            };
          }
        } catch (error) {
          console.error("Failed to poll generate-control status:", error);
          // keep polling until timeout
        }

        await new Promise((r) => setTimeout(r, intervalMs));
      }

      if (isMountedRef.current) setLoading(false);
      toast.error("Generation timed out", { id: toastId });
      return {
        status: lastStatus || "timeout",
      };
    },
    [navigate],
  );

  return { pollTask, loading };
};

// Hook for AI-enhancing an existing control with additional docs
export const useEnhanceControl = () => {
  const [loading, setLoading] = useState(false);

  const enhanceControl = useCallback(
    async (params: {
      controlId: string;
      controlDefFiles?: File[];
      pastWorkPaperFiles?: File[];
      additionalControlDetails?: string;
      generatePbc?: boolean;
    }): Promise<string | null> => {
      setLoading(true);
      try {
        const formData = new FormData();
        // Append multiple control definition files if provided
        if (params.controlDefFiles && params.controlDefFiles.length > 0) {
          params.controlDefFiles.forEach((file) => {
            formData.append("control_def_files", file);
          });
        }
        // Append multiple past workpaper files if provided
        if (params.pastWorkPaperFiles && params.pastWorkPaperFiles.length > 0) {
          params.pastWorkPaperFiles.forEach((file) => {
            formData.append("past_work_paper_files", file);
          });
        }
        if (params.additionalControlDetails) {
          formData.append(
            "additional_template_details",
            params.additionalControlDetails,
          );
        }
        if (params.generatePbc) {
          formData.append("generate_pbc", params.generatePbc.toString());
        }

        const response = await fetchFastApi({
          path: `/controls/v2/control/${params.controlId}/enhance`,
          method: "POST",
          body: formData,
        });
        const taskKey =
          response?.taskKey || response?.task_id || response?.taskId;

        if (!taskKey) {
          toast.error("Failed to enhance control: missing task key");
          return null;
        }

        return taskKey as string;
      } catch (error) {
        console.error("Failed to enhance control:", error);
        toast.error("Failed to enhance control");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    enhanceControl,
    loading,
  };
};

// Hook to poll the enhance-control task status until completion
export const usePollEnhanceControlStatus = () => {
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const pollTask = useCallback(
    async (
      taskKey: string,
      {
        intervalMs = 5000,
        timeoutMs = 10 * 60 * 1000,
        onSuccess,
      }: {
        intervalMs?: number;
        timeoutMs?: number;
        onSuccess?: () => void;
      } = {},
    ): Promise<{
      status: "success" | "failure" | "timeout";
      controlId?: string;
    }> => {
      if (isMountedRef.current) setLoading(true);
      const toastId = `enhance-control:${taskKey}`;
      toast.loading("Enhancing control...", {
        id: toastId,
        description:
          "We're enhancing your control. This may take a few minutes.",
      });
      const start = Date.now();
      let lastStatus: "success" | "failure" | "timeout" | null = null;

      while (Date.now() - start < timeoutMs) {
        try {
          console.log("polling task", taskKey);
          const response = await fetchFastApi({
            path: `/controls/v2/enhance-control/${encodeURIComponent(taskKey)}`,
            method: "GET",
          });
          const statusRaw: string = (
            response?.response?.status || "unknown"
          ).toString();
          const status = statusRaw.toLowerCase();
          lastStatus = status as "success" | "failure" | "timeout";

          if (status === "success") {
            if (isMountedRef.current) setLoading(false);
            const controlId = response?.response?.control_id;
            toast.success("Control enhanced", {
              id: toastId,
              description: "Your control has been enhanced successfully.",
              duration: 5000,
            });
            onSuccess?.();
            return {
              status: "success",
              controlId,
            };
          }
          if (status === "failure" || status === "cancelled") {
            if (isMountedRef.current) setLoading(false);
            toast.error("Failed to enhance control", { id: toastId });
            return {
              status: "failure",
            };
          }
        } catch (error) {
          console.error("Failed to poll enhance-control status:", error);
          // keep polling until timeout
        }

        await new Promise((r) => setTimeout(r, intervalMs));
      }

      if (isMountedRef.current) setLoading(false);
      toast.error("Enhancement timed out", { id: toastId });
      return {
        status: lastStatus || "timeout",
      };
    },
    [],
  );

  return { pollTask, loading };
};

// Session summary returned alongside the with-sessions list
export const ControlSessionSummarySchema = z.object({
  total: z.number(),
  complete: z.number(),
  inProgress: z.number(),
  inReview: z.number(),
});

export type ControlSessionSummary = z.infer<typeof ControlSessionSummarySchema>;

// Minimal session type returned by the with-sessions endpoint
export const DryControlSessionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z
    .enum(["in_progress", "in_review", "complete"])
    .nullable()
    .optional(),
  testPhase: z.enum(testPhaseValues).default("not_defined"),
  fyCycle: FyCycleOptionSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z
    .object({
      id: z.string(),
      email: z.string(),
      createdAt: z.string(),
      deactivatedAt: z.string().nullable(),
    })
    .optional()
    .nullable(),
});

export type DryControlSession = z.infer<typeof DryControlSessionSchema>;

export const ControlWithSessionsSchema = ControlWithUserSchema.extend({
  controlSessions: z.array(DryControlSessionSchema).default([]),
});

export type ControlWithSessions = z.infer<typeof ControlWithSessionsSchema>;

// Hook for fetching controls with their sessions (for fieldwork page)
export type SessionStatusFilter = "in_progress" | "in_review" | "complete";
export const useControlsWithSessions = (filters: {
  searchTerm?: string;
  cycles?: string[];
  processes?: string[];
  controlOwners?: string[];
  sessionStatuses?: SessionStatusFilter[];
  testPhases?: TestPhase[];
  testCreators?: string[];
  fyCycleIds?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}) => {
  const LIMIT = 25;

  const [controls, setControls] = useState<ControlWithSessions[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sessionSummary, setSessionSummary] =
    useState<ControlSessionSummary | null>(null);
  // See OptroAccessListFlagSchema: linked rows are hidden from this list.
  // Held until the list resets (mergeOptroAccessUnavailable).
  const [optroAccessUnavailable, setOptroAccessUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  // Incremented on every reset fetch; used to discard results from stale requests
  const generationRef = useRef(0);
  // Guards against concurrent loadMore calls only
  const isLoadingMoreRef = useRef(false);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchControls = useCallback(async (reset: boolean) => {
    if (!reset && isLoadingMoreRef.current) return;

    // For reset fetches, bump the generation so any in-flight request
    // from a previous filter selection knows to discard its results.
    if (reset) {
      generationRef.current += 1;
    }
    const generation = generationRef.current;

    if (!reset) {
      isLoadingMoreRef.current = true;
    }

    const currentOffset = reset ? 0 : offsetRef.current;
    const currentFilters = filtersRef.current;

    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        limit: LIMIT.toString(),
        offset: currentOffset.toString(),
      });

      if (currentFilters.searchTerm) {
        queryParams.append("search", currentFilters.searchTerm);
      }

      if (currentFilters.cycles && currentFilters.cycles.length > 0) {
        currentFilters.cycles.forEach((cycle) => {
          queryParams.append("cycles", cycle);
        });
      }

      if (currentFilters.processes && currentFilters.processes.length > 0) {
        currentFilters.processes.forEach((process) => {
          queryParams.append("processes", process);
        });
      }

      if (
        currentFilters.controlOwners &&
        currentFilters.controlOwners.length > 0
      ) {
        currentFilters.controlOwners.forEach((owner) => {
          queryParams.append("control_owners", owner);
        });
      }

      if (
        currentFilters.sessionStatuses &&
        currentFilters.sessionStatuses.length > 0
      ) {
        currentFilters.sessionStatuses.forEach((status) => {
          queryParams.append("session_statuses", status);
        });
      }

      if (currentFilters.testPhases && currentFilters.testPhases.length > 0) {
        currentFilters.testPhases.forEach((testPhase) => {
          queryParams.append("test_phases", testPhase);
        });
      }

      if (
        currentFilters.testCreators &&
        currentFilters.testCreators.length > 0
      ) {
        currentFilters.testCreators.forEach((creator) => {
          queryParams.append("created_by", creator);
        });
      }

      if (currentFilters.fyCycleIds && currentFilters.fyCycleIds.length > 0) {
        currentFilters.fyCycleIds.forEach((fyCycleId) => {
          queryParams.append("fy_cycle_ids", fyCycleId);
        });
      }

      if (currentFilters.sortBy) {
        queryParams.append("sort_by", currentFilters.sortBy);
      }

      if (currentFilters.sortOrder) {
        queryParams.append("sort_order", currentFilters.sortOrder);
      }

      const response = await fetchFastApi({
        path: `/controls/v2/with-sessions?${queryParams.toString()}`,
        method: "GET",
      });

      // Discard results from a superseded reset fetch
      if (generationRef.current !== generation) return;

      const data = z.array(ControlWithSessionsSchema).parse(response.controls);
      setTotalCount(response.totalCount);
      const pageUnavailable = readOptroAccessUnavailable(response);
      setOptroAccessUnavailable((current) =>
        mergeOptroAccessUnavailable(reset, current, pageUnavailable),
      );
      if (reset) {
        setSessionSummary(
          ControlSessionSummarySchema.parse(response.controlSessionSummary),
        );
      }

      setControls((prev) =>
        reset
          ? data
          : [...prev, ...data.filter((c) => !prev.some((e) => e.id === c.id))],
      );

      setHasMore(data.length === LIMIT);

      const newOffset = reset ? LIMIT : currentOffset + LIMIT;
      offsetRef.current = newOffset;
    } catch (error) {
      if (generationRef.current !== generation) return;
      console.error("Failed to fetch controls with sessions:", error);
      toast.error("Failed to fetch fieldwork data");
    } finally {
      if (generationRef.current === generation) {
        setLoading(false);
      }
      if (!reset) {
        isLoadingMoreRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setControls([]);
    fetchControls(true);
  }, [filtersKey, fetchControls]);

  const loadMore = useCallback(() => {
    if (isLoadingMoreRef.current || !hasMore) return;
    fetchControls(false);
  }, [hasMore, fetchControls]);

  return {
    controls,
    totalCount,
    sessionSummary,
    optroAccessUnavailable,
    loading,
    loadMore,
    hasMore,
    refetch: () => fetchControls(true),
  };
};

// Hook for fetching filter options
export const useControlFilterOptions = (
  includeNonTemplates: boolean = false,
) => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const fetchFilterOptions = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams();
      if (includeNonTemplates) {
        queryParams.append("include_non_templates", "true");
      }

      const response = await fetchFastApi({
        path: `/controls/v2/filter-options?${queryParams.toString()}`,
        method: "GET",
      });

      const data = FilterOptionsSchema.parse(response.filterOptions);
      setFilterOptions(data);
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
      toast.error("Failed to fetch filter options");
      setFilterOptions(null);
    } finally {
      setLoading(false);
    }
  }, [includeNonTemplates]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  return {
    filterOptions,
    loading,
    refetch: fetchFilterOptions,
  };
};
